import { apiClient } from '@/services/api-client';

/**
 * Phase 12.7b Wave 2 — Email Delivery Log API (D-123).
 *
 * `apiClient`'s response interceptor already unwraps `{ data, meta }`, so
 * `r.data` IS the payload — a second `.data` access would yield `undefined`.
 *
 * **Read-only by construction.** There is exactly one function and it is a GET.
 * OD-1b makes this view-only: no retry, resend or delete exists to call.
 */

export type EmailDeliveryStatus = 'SENT' | 'FAILED';

/**
 * One delivery attempt.
 *
 * **No body, subject, attachment or provider payload field exists** — the server
 * never stores them (OD-1c), so there is nothing to type here.
 */
export interface EmailDeliveryLogEntry {
  id: string;
  recipient: string;
  purpose: string;
  status: EmailDeliveryStatus;
  gatewayId: string | null;
  gatewayScope: string | null;
  gatewayOrigin: string | null;
  errorMessage: string | null;
  occurredAt: string;
}

export interface EmailDeliveryLogPage {
  items: EmailDeliveryLogEntry[];
  total: number;
  limit: number;
  offset: number;
  /** Retention window, so the page can state the limit rather than imply forever. */
  retentionDays: number;
}

export interface EmailDeliveryLogQuery {
  limit?: number;
  offset?: number;
  status?: EmailDeliveryStatus;
  from?: string;
  to?: string;
}

export async function fetchEmailDeliveryLogs(
  query: EmailDeliveryLogQuery,
  signal?: AbortSignal,
): Promise<EmailDeliveryLogPage> {
  const params: Record<string, string | number> = {};
  if (query.limit !== undefined) params['limit'] = query.limit;
  if (query.offset !== undefined) params['offset'] = query.offset;
  if (query.status !== undefined) params['status'] = query.status;
  if (query.from !== undefined && query.from !== '') params['from'] = query.from;
  if (query.to !== undefined && query.to !== '') params['to'] = query.to;

  const r = await apiClient.get('/email-delivery-logs', {
    params,
    ...(signal ? { signal } : {}),
  });

  const body = r.data as Partial<EmailDeliveryLogPage> | null | undefined;
  // Guarded: a malformed body renders as "no records", never as a crash — and
  // never as a silently empty page that could read as "no email was sent".
  return {
    items: Array.isArray(body?.items) ? (body.items as EmailDeliveryLogEntry[]) : [],
    total: typeof body?.total === 'number' ? body.total : 0,
    limit: typeof body?.limit === 'number' ? body.limit : (query.limit ?? 20),
    offset: typeof body?.offset === 'number' ? body.offset : (query.offset ?? 0),
    retentionDays: typeof body?.retentionDays === 'number' ? body.retentionDays : 0,
  };
}

/** `[from, to)` from two date inputs — the end day is included by advancing one day. */
export function toDeliveryRange(fromDate: string, toDate: string): { from?: string; to?: string } {
  const out: { from?: string; to?: string } = {};
  if (fromDate) {
    const d = new Date(`${fromDate}T00:00:00.000Z`);
    if (!Number.isNaN(d.getTime())) out.from = d.toISOString();
  }
  if (toDate) {
    const d = new Date(`${toDate}T00:00:00.000Z`);
    if (!Number.isNaN(d.getTime())) {
      d.setUTCDate(d.getUTCDate() + 1);
      out.to = d.toISOString();
    }
  }
  return out;
}
